import React, { useRef, useEffect, useState, useCallback } from 'react'
import { AnnotationTool, Annotation, Point } from './types'

interface AnnotationCanvasProps {
  width: number
  height: number
  backgroundImage?: string
  annotations: Annotation[]
  currentTool: AnnotationTool
  toolColor: string
  toolSize: number
  onAnnotationAdd: (annotation: Annotation) => void
  onAnnotationUpdate: (id: string, updates: Partial<Annotation>) => void
  onAnnotationDelete: (id: string) => void
  onAnnotationSelect: (id: string | null) => void
  selectedAnnotationId: string | null
  readonly?: boolean
}

export const AnnotationCanvas: React.FC<AnnotationCanvasProps> = ({
  width,
  height,
  backgroundImage,
  annotations,
  currentTool,
  toolColor,
  toolSize,
  onAnnotationAdd,
  onAnnotationUpdate,
  onAnnotationDelete,
  onAnnotationSelect,
  selectedAnnotationId,
  readonly = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const overlayRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [currentPoints, setCurrentPoints] = useState<Point[]>([])
  const [dragState, setDragState] = useState<{
    annotationId: string
    startPoint: Point
    originalPoints: Point[]
  } | null>(null)

  // Draw background image
  useEffect(() => {
    if (!backgroundImage || !canvasRef.current) return
    
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const img = new Image()
    img.onload = () => {
      ctx.clearRect(0, 0, width, height)
      ctx.drawImage(img, 0, 0, width, height)
    }
    img.src = backgroundImage
  }, [backgroundImage, width, height])

  // Draw all annotations
  useEffect(() => {
    if (!overlayRef.current) return
    
    const canvas = overlayRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, width, height)
    
    annotations.forEach(annotation => {
      drawAnnotation(ctx, annotation, annotation.id === selectedAnnotationId)
    })
  }, [annotations, selectedAnnotationId, width, height])

  const drawAnnotation = (ctx: CanvasRenderingContext2D, annotation: Annotation, isSelected: boolean) => {
    ctx.strokeStyle = annotation.color
    ctx.fillStyle = annotation.color
    ctx.lineWidth = annotation.size
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    // Add selection highlight
    if (isSelected) {
      ctx.shadowColor = '#3b82f6'
      ctx.shadowBlur = 10
    } else {
      ctx.shadowColor = 'transparent'
      ctx.shadowBlur = 0
    }

    switch (annotation.type) {
      case 'pen':
        drawPenStroke(ctx, annotation.points)
        break
      case 'arrow':
        drawArrow(ctx, annotation.points[0], annotation.points[1])
        break
      case 'line':
        drawLine(ctx, annotation.points[0], annotation.points[1])
        break
      case 'rectangle':
        drawRectangle(ctx, annotation.points[0], annotation.points[1])
        break
      case 'circle':
        drawCircle(ctx, annotation.points[0], annotation.points[1])
        break
      case 'text':
        drawText(ctx, annotation.points[0], annotation.text || '', annotation.size)
        break
    }

    // Draw selection handles
    if (isSelected && !readonly) {
      drawSelectionHandles(ctx, annotation)
    }
  }

  const drawPenStroke = (ctx: CanvasRenderingContext2D, points: Point[]) => {
    if (points.length < 2) return
    
    ctx.beginPath()
    ctx.moveTo(points[0].x, points[0].y)
    
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y)
    }
    
    ctx.stroke()
  }

  const drawArrow = (ctx: CanvasRenderingContext2D, start: Point, end: Point) => {
    // Draw line
    ctx.beginPath()
    ctx.moveTo(start.x, start.y)
    ctx.lineTo(end.x, end.y)
    ctx.stroke()

    // Draw arrowhead
    const angle = Math.atan2(end.y - start.y, end.x - start.x)
    const arrowLength = 15
    const arrowAngle = Math.PI / 6

    ctx.beginPath()
    ctx.moveTo(end.x, end.y)
    ctx.lineTo(
      end.x - arrowLength * Math.cos(angle - arrowAngle),
      end.y - arrowLength * Math.sin(angle - arrowAngle)
    )
    ctx.moveTo(end.x, end.y)
    ctx.lineTo(
      end.x - arrowLength * Math.cos(angle + arrowAngle),
      end.y - arrowLength * Math.sin(angle + arrowAngle)
    )
    ctx.stroke()
  }

  const drawLine = (ctx: CanvasRenderingContext2D, start: Point, end: Point) => {
    ctx.beginPath()
    ctx.moveTo(start.x, start.y)
    ctx.lineTo(end.x, end.y)
    ctx.stroke()
  }

  const drawRectangle = (ctx: CanvasRenderingContext2D, start: Point, end: Point) => {
    const width = end.x - start.x
    const height = end.y - start.y
    ctx.beginPath()
    ctx.rect(start.x, start.y, width, height)
    ctx.stroke()
  }

  const drawCircle = (ctx: CanvasRenderingContext2D, start: Point, end: Point) => {
    const radius = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2))
    ctx.beginPath()
    ctx.arc(start.x, start.y, radius, 0, 2 * Math.PI)
    ctx.stroke()
  }

  const drawText = (ctx: CanvasRenderingContext2D, point: Point, text: string, size: number) => {
    ctx.font = `${size * 2}px Arial`
    ctx.textBaseline = 'top'
    ctx.fillText(text, point.x, point.y)
  }

  const drawSelectionHandles = (ctx: CanvasRenderingContext2D, annotation: Annotation) => {
    const handleSize = 6
    ctx.fillStyle = '#3b82f6'
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 2

    // Get bounds of annotation
    const bounds = getAnnotationBounds(annotation)
    const handles = [
      { x: bounds.minX, y: bounds.minY },
      { x: bounds.maxX, y: bounds.minY },
      { x: bounds.maxX, y: bounds.maxY },
      { x: bounds.minX, y: bounds.maxY }
    ]

    handles.forEach(handle => {
      ctx.beginPath()
      ctx.arc(handle.x, handle.y, handleSize, 0, 2 * Math.PI)
      ctx.fill()
      ctx.stroke()
    })
  }

  const getAnnotationBounds = (annotation: Annotation) => {
    const xs = annotation.points.map(p => p.x)
    const ys = annotation.points.map(p => p.y)
    
    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys)
    }
  }

  const getMousePoint = (e: React.MouseEvent): Point => {
    const rect = overlayRef.current?.getBoundingClientRect()
    if (!rect) return { x: 0, y: 0 }
    
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    }
  }

  const findAnnotationAt = (point: Point): string | null => {
    for (let i = annotations.length - 1; i >= 0; i--) {
      const annotation = annotations[i]
      if (isPointInAnnotation(point, annotation)) {
        return annotation.id
      }
    }
    return null
  }

  const isPointInAnnotation = (point: Point, annotation: Annotation): boolean => {
    const bounds = getAnnotationBounds(annotation)
    const margin = Math.max(annotation.size, 10)
    
    return point.x >= bounds.minX - margin &&
           point.x <= bounds.maxX + margin &&
           point.y >= bounds.minY - margin &&
           point.y <= bounds.maxY + margin
  }

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (readonly) return
    
    const point = getMousePoint(e)
    
    // Check if clicking on existing annotation for selection/dragging
    const clickedAnnotationId = findAnnotationAt(point)
    
    if (currentTool === 'select') {
      onAnnotationSelect(clickedAnnotationId)
      
      if (clickedAnnotationId) {
        const annotation = annotations.find(a => a.id === clickedAnnotationId)
        if (annotation) {
          setDragState({
            annotationId: clickedAnnotationId,
            startPoint: point,
            originalPoints: annotation.points
          })
        }
      }
      return
    }

    // Start drawing new annotation
    setIsDrawing(true)
    setCurrentPoints([point])
    
    if (currentTool === 'text') {
      const text = prompt('Enter text:')
      if (text) {
        const newAnnotation: Annotation = {
          id: `annotation-${Date.now()}`,
          type: 'text',
          points: [point],
          color: toolColor,
          size: toolSize,
          text,
          createdAt: new Date().toISOString()
        }
        onAnnotationAdd(newAnnotation)
      }
      setIsDrawing(false)
    }
  }, [currentTool, toolColor, toolSize, readonly, annotations, onAnnotationAdd, onAnnotationSelect])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (readonly) return
    
    const point = getMousePoint(e)
    
    // Handle dragging selected annotation
    if (dragState) {
      const deltaX = point.x - dragState.startPoint.x
      const deltaY = point.y - dragState.startPoint.y
      
      const newPoints = dragState.originalPoints.map(p => ({
        x: p.x + deltaX,
        y: p.y + deltaY
      }))
      
      onAnnotationUpdate(dragState.annotationId, { points: newPoints })
      return
    }

    if (!isDrawing) return

    if (currentTool === 'pen') {
      setCurrentPoints(prev => [...prev, point])
    } else {
      setCurrentPoints(prev => [prev[0], point])
    }
  }, [currentTool, isDrawing, dragState, readonly, onAnnotationUpdate])

  const handleMouseUp = useCallback(() => {
    if (readonly) return
    
    if (dragState) {
      setDragState(null)
      return
    }

    if (isDrawing && currentPoints.length >= 2) {
      const newAnnotation: Annotation = {
        id: `annotation-${Date.now()}`,
        type: currentTool as any,
        points: currentPoints,
        color: toolColor,
        size: toolSize,
        createdAt: new Date().toISOString()
      }
      
      onAnnotationAdd(newAnnotation)
    }
    
    setIsDrawing(false)
    setCurrentPoints([])
  }, [isDrawing, currentPoints, currentTool, toolColor, toolSize, dragState, readonly, onAnnotationAdd])

  // Draw current drawing preview
  useEffect(() => {
    if (!overlayRef.current || !isDrawing || currentPoints.length === 0) return
    
    const canvas = overlayRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Save current state, draw preview, restore state
    ctx.save()
    ctx.strokeStyle = toolColor
    ctx.lineWidth = toolSize
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.setLineDash([5, 5]) // Dashed preview line

    if (currentTool === 'pen' && currentPoints.length > 1) {
      drawPenStroke(ctx, currentPoints)
    } else if (currentPoints.length === 2) {
      const [start, end] = currentPoints
      switch (currentTool) {
        case 'arrow':
          drawArrow(ctx, start, end)
          break
        case 'line':
          drawLine(ctx, start, end)
          break
        case 'rectangle':
          drawRectangle(ctx, start, end)
          break
        case 'circle':
          drawCircle(ctx, start, end)
          break
      }
    }

    ctx.restore()
  }, [currentPoints, currentTool, toolColor, toolSize, isDrawing])

  return (
    <div className="relative" style={{ width, height }}>
      {/* Background canvas for screenshot/image */}
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="absolute inset-0 border border-gray-300 rounded"
      />
      
      {/* Overlay canvas for annotations */}
      <canvas
        ref={overlayRef}
        width={width}
        height={height}
        className="absolute inset-0 cursor-crosshair"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
      
      {/* Delete button for selected annotation */}
      {selectedAnnotationId && !readonly && (
        <button
          className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-sm hover:bg-red-600"
          onClick={() => onAnnotationDelete(selectedAnnotationId)}
        >
          Delete
        </button>
      )}
    </div>
  )
}